import moment from 'moment-timezone';

/**
 * Utilitaires d'ajout à l'agenda (génération .ics + URLs Google/Outlook).
 * Miroir de la logique Talkizy (resources/js/lib/calendar.ts) afin que la liste
 * des calendriers soit identique des deux côtés.
 */

function formatDateToICS(date) {
  return date
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
}

function escapeICS(text) {
  return String(text)
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

export function generateICS(event) {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Teachizy//Meeting//FR',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `DTSTART:${formatDateToICS(event.startDate)}`,
    `DTEND:${formatDateToICS(event.endDate)}`,
    `SUMMARY:${escapeICS(event.title)}`,
  ];

  if (event.description) {
    lines.push(`DESCRIPTION:${escapeICS(event.description)}`);
  }

  if (event.location) {
    lines.push(`LOCATION:${escapeICS(event.location)}`);
  }

  if (event.url) {
    lines.push(`URL:${event.url}`);
  }

  lines.push(
    `UID:${Date.now()}@teachizy.fr`,
    `DTSTAMP:${formatDateToICS(new Date())}`,
    'END:VEVENT',
    'END:VCALENDAR',
  );

  return lines.join('\r\n');
}

export function downloadICS(event, filename) {
  const blob = new Blob([generateICS(event)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `${event.title.replace(/[^a-zA-Z0-9]/g, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

export function getGoogleCalendarUrl(event) {
  const startDate = formatDateToICS(event.startDate);
  const endDate = formatDateToICS(event.endDate);

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${startDate}/${endDate}`,
  });

  if (event.description) {
    params.set('details', event.description);
  }

  if (event.location || event.url) {
    params.set('location', event.location ?? event.url ?? '');
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function getOutlookCalendarUrl(event) {
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: event.title,
    startdt: event.startDate.toISOString(),
    enddt: event.endDate.toISOString(),
  });

  if (event.description) {
    params.set('body', event.description);
  }

  if (event.location || event.url) {
    params.set('location', event.location ?? event.url ?? '');
  }

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

export function getYahooCalendarUrl(event) {
  const params = new URLSearchParams({
    v: '60',
    title: event.title,
    st: formatDateToICS(event.startDate),
    et: formatDateToICS(event.endDate),
  });

  if (event.description) {
    params.set('desc', event.description);
  }

  if (event.location || event.url) {
    params.set('in_loc', event.location ?? event.url ?? '');
  }

  return `https://calendar.yahoo.com/?${params.toString()}`;
}

/**
 * Événements du calendrier (shape API snake_case : start_date/end_date UTC,
 * timezone, frequency, location_value, is_all_day).
 */

const DEFAULT_EVENT_DURATION_MINUTES = 60;

function stripHtml(html) {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  return (doc.body.textContent || '').trim();
}

/**
 * Convertit un événement API vers le format attendu par les helpers
 * d'ajout à l'agenda ci-dessus ({ title, startDate, endDate, location, url }).
 */
export function calendarEventToPayload(event) {
  const isUrl = /^https?:\/\//i.test(event.location_value || '');
  const tz = event.timezone || 'Europe/Paris';
  let startDate;
  let endDate;

  if (event.is_all_day) {
    startDate = moment.utc(event.start_date).tz(tz).startOf('day').toDate();
    endDate = moment.utc(event.end_date || event.start_date).tz(tz).endOf('day').toDate();
  } else {
    startDate = moment.utc(event.start_date).toDate();
    endDate = event.end_date
      ? moment.utc(event.end_date).toDate()
      : moment.utc(event.start_date).add(DEFAULT_EVENT_DURATION_MINUTES, 'minutes').toDate();
  }

  return {
    title: event.title,
    description: stripHtml(event.description),
    startDate,
    endDate,
    location: isUrl ? undefined : (event.location_value || undefined),
    url: isUrl ? event.location_value : undefined,
  };
}

export function generateGoogleCalendarUrl(event) {
  return getGoogleCalendarUrl(calendarEventToPayload(event));
}

export function generateOutlookCalendarUrl(event) {
  return getOutlookCalendarUrl(calendarEventToPayload(event));
}

export function generateYahooCalendarUrl(event) {
  return getYahooCalendarUrl(calendarEventToPayload(event));
}

export function generateICSFile(event) {
  const tz = event.timezone || 'Europe/Paris';
  const date = moment.utc(event.start_date).tz(tz).format('YYYY-MM-DD');
  const safeTitle = (event.title || 'evenement').replace(/[^a-zA-Z0-9]/g, '_');
  const safeTz = tz.replace(/[^a-zA-Z0-9+-]/g, '-');

  downloadICS(calendarEventToPayload(event), `${safeTitle}_${date}_${safeTz}.ics`);
}

/**
 * Développe les événements récurrents en occurrences comprises dans
 * l'intervalle [rangeStart, rangeEnd]. Les événements uniques sont
 * retournés tels quels.
 */
export function expandRecurringCalendarEvents(events, rangeStart, rangeEnd) {
  const result = [];
  const rStart = moment.utc(rangeStart);
  // La borne de fin est inclusive : "2026-09-06" couvre toute la journée du 6
  const rEnd = moment.utc(rangeEnd).endOf('day');

  const incrementMap = {
    daily: { amount: 1, unit: 'days' },
    weekly: { amount: 1, unit: 'weeks' },
    monthly: { amount: 1, unit: 'months' },
    yearly: { amount: 1, unit: 'years' },
  };

  events.forEach((event) => {
    const tz = event.timezone || 'Europe/Paris';
    const evStart = moment.utc(event.start_date).tz(tz);
    const evEnd = event.end_date ? moment.utc(event.end_date).tz(tz) : evStart.clone();
    const duration = evEnd.diff(evStart, 'minutes');

    if (!event.frequency || event.frequency === 'once') {
      result.push(event);
      return;
    }

    const inc = incrementMap[event.frequency];
    const limit = 400;

    // Mensuel/annuel : chaque occurrence est ancrée sur la date d'origine pour
    // qu'un mois court (ex : 31 → 30) ne décale pas les suivantes, et les mois
    // où le jour n'existe pas sont ignorés (comme annoncé dans le formulaire)
    if (event.frequency === 'monthly' || event.frequency === 'yearly') {
      let n = 0;

      if (rStart.isAfter(evStart)) {
        const unitsBehind = rStart.diff(evStart, inc.unit);

        if (unitsBehind > 1) {
          n = unitsBehind - 1;
        }
      }

      for (let i = 0; i < limit; i++) {
        const occStart = evStart.clone().add(n, inc.unit);
        n += 1;

        if (occStart.isAfter(rEnd)) break;

        if (occStart.date() === evStart.date()) {
          const occEnd = occStart.clone().add(duration, 'minutes');

          if (occStart.isSameOrAfter(rStart, 'day') || occEnd.isSameOrAfter(rStart, 'day')) {
            result.push({
              ...event,
              start_date: occStart.utc().format('YYYY-MM-DD HH:mm:ss'),
              end_date: event.end_date ? occEnd.utc().format('YYYY-MM-DD HH:mm:ss') : null,
              _occurrence: true,
            });
          }
        }
      }
      return;
    }

    // 1er / dernier jour du mois : littéral (comme les libellés), à l'heure
    // de départ ; les mois précédant la date de début sont ignorés
    if (event.frequency === 'first_of_month' || event.frequency === 'last_of_month') {
      let n = 0;

      if (rStart.isAfter(evStart)) {
        const unitsBehind = rStart.diff(evStart, 'months');

        if (unitsBehind > 1) {
          n = unitsBehind - 1;
        }
      }

      for (let i = 0; i < limit; i++) {
        const monthStart = evStart.clone().startOf('month').add(n, 'months');
        n += 1;

        const occStart = event.frequency === 'first_of_month'
          ? monthStart.clone()
          : monthStart.clone().endOf('month').startOf('day');

        occStart.hour(evStart.hour()).minute(evStart.minute()).second(evStart.second());

        if (occStart.isAfter(rEnd)) break;

        if (!occStart.isBefore(evStart, 'day')) {
          const occEnd = occStart.clone().add(duration, 'minutes');

          if (occStart.isSameOrAfter(rStart, 'day') || occEnd.isSameOrAfter(rStart, 'day')) {
            result.push({
              ...event,
              start_date: occStart.utc().format('YYYY-MM-DD HH:mm:ss'),
              end_date: event.end_date ? occEnd.utc().format('YYYY-MM-DD HH:mm:ss') : null,
              _occurrence: true,
            });
          }
        }
      }
      return;
    }

    const cursor = evStart.clone();

    // Avance rapide juste avant le début de l'intervalle pour les fréquences
    // simples (sinon un événement quotidien ancien épuiserait la limite d'itérations)
    if (inc && rStart.isAfter(evStart)) {
      const unitsBehind = rStart.diff(evStart, inc.unit);

      if (unitsBehind > 1) {
        cursor.add((unitsBehind - 1) * inc.amount, inc.unit);
      }
    }

    let count = 0;

    while (cursor.isSameOrBefore(rEnd) && count < limit) {
      const occStart = cursor.clone();
      const occEnd = cursor.clone().add(duration, 'minutes');

      if (occStart.isSameOrAfter(rStart, 'day') || occEnd.isSameOrAfter(rStart, 'day')) {
        result.push({
          ...event,
          start_date: occStart.utc().format('YYYY-MM-DD HH:mm:ss'),
          end_date: event.end_date ? occEnd.utc().format('YYYY-MM-DD HH:mm:ss') : null,
          _occurrence: true,
        });
      }

      if (inc) {
        cursor.add(inc.amount, inc.unit);
      } else {
        break;
      }

      count += 1;
    }
  });

  return result;
}

/**
 * Formate les dates d'un événement pour l'affichage.
 * - Sans displayTimezone → convertit l'UTC vers l'heure locale du navigateur
 * - Avec displayTimezone → convertit l'UTC vers cette timezone
 */
export function formatCalendarEventDates(event, displayTimezone) {
  let start;
  let end;
  let tzLabel;

  if (displayTimezone) {
    start = moment.utc(event.start_date).tz(displayTimezone);
    end = event.end_date ? moment.utc(event.end_date).tz(displayTimezone) : start.clone();
    tzLabel = displayTimezone;
  } else {
    start = moment.utc(event.start_date).local();
    end = event.end_date ? moment.utc(event.end_date).local() : start.clone();
    tzLabel = null;
  }

  const sameDay = start.isSame(end, 'day');
  const suffix = tzLabel ? ` (${tzLabel})` : '';

  if (event.is_all_day) {
    if (sameDay) {
      return `${start.format('DD/MM/YYYY')}${suffix}`;
    }
    return `Du ${start.format('DD/MM/YYYY')} au ${end.format('DD/MM/YYYY')}${suffix}`;
  }

  if (sameDay) {
    if (!event.end_date || start.isSame(end)) {
      return `${start.format('DD/MM/YYYY')} à ${start.format('HH:mm')}${suffix}`;
    }
    return `${start.format('DD/MM/YYYY')} de ${start.format('HH:mm')} à ${end.format('HH:mm')}${suffix}`;
  }
  return `Du ${start.format('DD/MM/YYYY HH:mm')} au ${end.format('DD/MM/YYYY HH:mm')}${suffix}`;
}
